import React, { useState, useEffect, useMemo } from 'react';
import { Clock, Loader2, AlertCircle } from 'lucide-react';
import { Drawer, DateRangePicker } from '../ui';
import RecipientPicker from './RecipientPicker';
import DynamicParameterField from './DynamicParameterField';
import useGlobalCustomerStore from '../../stores/globalCustomerStore';
import useSocialMediaStore from '../../stores/socialMediaStore';
import usePermissionsStore from '../../stores/permissionsStore';
import { useToast } from '../../contexts/ToastContext';
import { useNavigationTracking } from '../../contexts/AnalyticsContext';
import { ScheduledReport, Period, RecipientReference, ReportType } from '../../types/report';

interface ScheduleReportDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  reportTitle: string;
  reportTypeDefinition?: ReportType;
  onSchedule: (config: ReportScheduleConfig) => Promise<void>;
  // Edit mode props
  editMode?: boolean;
  existingSchedule?: ScheduledReport | null;
  onUpdate?: (scheduleId: string, config: ReportScheduleConfig) => Promise<void>;
}

export interface ReportScheduleConfig {
  customerId: string;
  reportName?: string;
  period: Period;
  customStartDate?: string;
  customEndDate?: string;
  accountIds?: string[];
  postTypes?: string[];
  scheduleType: 'one-time' | 'recurring';
  oneTimeDate?: string;
  oneTimeTime?: string;
  recurringFrequency?: 'daily' | 'weekly' | 'monthly';
  recurringDay?: number; // Day of week (0-6) or day of month (1-31)
  recurringTime?: string;
  timezone?: string; // IANA timezone (e.g., 'America/New_York')
  emailReport: boolean;
  emailAddress?: string;
  recipientRefs?: RecipientReference[];
  dynamicParams?: Record<string, any>;
}

const ScheduleReportDrawer: React.FC<ScheduleReportDrawerProps> = ({
  isOpen,
  onClose,
  reportTitle,
  reportTypeDefinition,
  onSchedule,
  editMode = false,
  existingSchedule,
  onUpdate
}) => {
  const { showError } = useToast();
  const { trackDrawerOpened } = useNavigationTracking();
  const { permissions } = usePermissionsStore();
  const { selectedCustomer, customerAccounts } = useGlobalCustomerStore();
  const accounts = selectedCustomer ? customerAccounts[selectedCustomer.id] || [] : [];
  const {
    currentCustomerId,
    availablePostTypes,
    loadAvailablePostTypes,
    setCustomerId
  } = useSocialMediaStore();

  // Local state for filters
  const [period, setPeriod] = useState<Period>('month');
  const [customDateRange, setCustomDateRange] = useState<{ start: string; end: string } | null>(null);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [selectedPostType, setSelectedPostType] = useState<string>('all');

  // Schedule state
  const [scheduleType, setScheduleType] = useState<'one-time' | 'recurring'>('one-time');
  const [oneTimeDate, setOneTimeDate] = useState('');
  const [oneTimeTime, setOneTimeTime] = useState('09:00');
  const [recurringFrequency, setRecurringFrequency] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [recurringDay, setRecurringDay] = useState(1); // Monday for weekly, 1st for monthly
  const [recurringTime, setRecurringTime] = useState('09:00');
  const [reportName, setReportName] = useState('');
  const [recipientRefs, setRecipientRefs] = useState<RecipientReference[]>([]);
  const [dynamicParams, setDynamicParams] = useState<Record<string, any>>({});

  const [isScheduling, setIsScheduling] = useState(false);

  // Sync customer ID from global store when drawer opens
  useEffect(() => {
    if (isOpen && selectedCustomer?.id && selectedCustomer.id !== currentCustomerId) {
      setCustomerId(selectedCustomer.id);
    }
  }, [isOpen, selectedCustomer?.id, currentCustomerId, setCustomerId]);

  // Track drawer opened
  useEffect(() => {
    if (isOpen) {
      trackDrawerOpened('schedule_report', { reportTitle, editMode });
    }
  }, [isOpen, reportTitle, editMode, trackDrawerOpened]);

  // Load available filters when drawer opens
  useEffect(() => {
    if (isOpen && currentCustomerId) {
      loadAvailablePostTypes();
    }
  }, [isOpen, currentCustomerId]);

  // Initialize dynamic parameters with default values
  useEffect(() => {
    if (isOpen && !editMode && reportTypeDefinition?.metadata.parameters) {
      const initialParams: Record<string, any> = {};
      Object.entries(reportTypeDefinition.metadata.parameters).forEach(([key, def]) => {
        if (def.defaultValue !== undefined) {
          initialParams[key] = def.defaultValue;
        }
      });
      setDynamicParams(initialParams);
    }
  }, [isOpen, editMode, reportTypeDefinition]);

  // Pre-populate form when editing an existing schedule
  useEffect(() => {
    if (editMode && existingSchedule && isOpen) {
      // Set period and date range
      setPeriod(existingSchedule.parameters.period);
      if (existingSchedule.parameters.period === 'custom') {
        setCustomDateRange({
          start: existingSchedule.parameters.customStartDate || '',
          end: existingSchedule.parameters.customEndDate || ''
        });
      }

      // Set post type
      if (existingSchedule.parameters.postTypes?.length) {
        setSelectedPostType(existingSchedule.parameters.postTypes[0]);
      } else {
        setSelectedPostType('all');
      }

      // Set accounts
      if (existingSchedule.parameters.accountIds?.length) {
        setSelectedAccountIds(existingSchedule.parameters.accountIds);
      } else {
        setSelectedAccountIds([]);
      }

      // Set schedule type and details
      const schedule = existingSchedule.schedule;
      if (schedule.type === 'one-time') {
        setScheduleType('one-time');
        setOneTimeDate(schedule.oneTimeDate || '');
        setOneTimeTime(schedule.oneTimeTime || '09:00');
      } else {
        setScheduleType('recurring');
        setRecurringFrequency(schedule.type as 'daily' | 'weekly' | 'monthly');
        setRecurringDay(schedule.recurringDay || 1);
        setRecurringTime(schedule.recurringTime || '09:00');
      }

      // Set report name and recipients
      setReportName(existingSchedule.reportName || '');
      setRecipientRefs(existingSchedule.emailConfig.recipientRefs || []);

      // Populate dynamic params from existing schedule parameters
      if (reportTypeDefinition?.metadata.parameters) {
        const params: Record<string, any> = {};
        Object.keys(reportTypeDefinition.metadata.parameters).forEach(key => {
          if (existingSchedule.parameters[key] !== undefined) {
            params[key] = existingSchedule.parameters[key];
          }
        });
        setDynamicParams(params);
      }
    } else if (!editMode && isOpen) {
      // Reset form for new schedule
      setPeriod('month');
      setCustomDateRange(null);
      setSelectedAccountIds(accounts.map(a => a.id)); // Default to all accounts
      setSelectedPostType('all');
      setScheduleType('one-time');
      setOneTimeDate('');
      setOneTimeTime('09:00');
      setRecurringFrequency('weekly');
      setRecurringDay(1);
      setRecurringTime('09:00');
      setReportName('');
      setRecipientRefs([]);
    }
  }, [editMode, existingSchedule, isOpen, accounts]);

  // Set all accounts selected by default when accounts change or drawer opens (for new schedules)
  useEffect(() => {
    if (isOpen && !editMode && accounts.length > 0) {
      setSelectedAccountIds(accounts.map(a => a.id));
    }
  }, [isOpen, editMode, accounts.length]);

  // Reset form to defaults
  const resetForm = () => {
    setPeriod('month');
    setCustomDateRange(null);
    setSelectedAccountIds(accounts.map(a => a.id)); // Default to all accounts
    setSelectedPostType('all');
    setScheduleType('one-time');
    setOneTimeDate('');
    setOneTimeTime('09:00');
    setRecurringFrequency('weekly');
    setRecurringDay(1);
    setRecurringTime('09:00');
    setReportName('');
    setRecipientRefs([]);
    setDynamicParams({});
  };

  // Handle close with reset
  const handleDrawerClose = () => {
    resetForm();
    onClose();
  };

  // Check if all required dynamic parameters are filled
  const isFormValid = useMemo(() => {
    if (!currentCustomerId) return false;

    if (reportTypeDefinition?.metadata.parameters) {
      return Object.entries(reportTypeDefinition.metadata.parameters).every(([key, def]) => {
        if (!def.required) return true;
        const value = dynamicParams[key];
        if (value === undefined || value === null || value === '') return false;
        if (Array.isArray(value) && value.length === 0) return false;
        return true;
      });
    }

    return true;
  }, [currentCustomerId, reportTypeDefinition, dynamicParams]);

  const handleSchedule = async () => {
    if (!currentCustomerId || !selectedCustomer) {
      showError('No customer selected', 'Please select a customer to schedule a report.');
      return;
    }

    // Validate required dynamic parameters
    if (reportTypeDefinition?.metadata.parameters) {
      const missingParams: string[] = [];
      Object.entries(reportTypeDefinition.metadata.parameters).forEach(([key, def]) => {
        if (def.required && !dynamicParams[key]) {
          missingParams.push(def.label);
        }
      });
      if (missingParams.length > 0) {
        showError('Missing required fields', `Please provide: ${missingParams.join(', ')}`);
        return;
      }
    }

    // Validation
    if (scheduleType === 'one-time' && !oneTimeDate) {
      showError('Date required', 'Please select a date for the one-time report.');
      return;
    }

    if (recipientRefs.length === 0) {
      showError('Recipients required', 'Please select at least one recipient for the report.');
      return;
    }

    setIsScheduling(true);

    try {
      // Get the user's timezone from the browser
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const config: ReportScheduleConfig = {
        customerId: currentCustomerId,
        reportName: reportName.trim() || undefined,
        period: period,
        customStartDate: customDateRange?.start,
        customEndDate: customDateRange?.end,
        accountIds: selectedAccountIds.length > 0 ? selectedAccountIds : undefined,
        postTypes: selectedPostType && selectedPostType !== 'all' ? [selectedPostType] : undefined,
        scheduleType,
        oneTimeDate: scheduleType === 'one-time' ? oneTimeDate : undefined,
        oneTimeTime: scheduleType === 'one-time' ? oneTimeTime : undefined,
        recurringFrequency: scheduleType === 'recurring' ? recurringFrequency : undefined,
        recurringDay: scheduleType === 'recurring' ? recurringDay : undefined,
        recurringTime: scheduleType === 'recurring' ? recurringTime : undefined,
        timezone: userTimezone,
        emailReport: true,
        recipientRefs,
        dynamicParams,
      };

      if (editMode && existingSchedule && onUpdate) {
        await onUpdate(existingSchedule.id, config);
      } else {
        await onSchedule(config);
      }
      onClose();
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsScheduling(false);
    }
  };

  const getDayOfWeekName = (day: number) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[day];
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleDrawerClose}
      title={editMode ? `Edit Schedule: ${reportTitle}` : `Schedule ${reportTitle}`}
      description={editMode ? "Update your scheduled report settings" : "Set up automated report generation"}
      icon={<Clock className="h-5 w-5" />}
      width="lg"
    >
      <div className="space-y-6">
        {/* No Customer Selected Warning */}
        {!selectedCustomer && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">No customer selected</p>
              <p className="text-xs text-amber-600 mt-1">Please select a customer from the header to schedule a report.</p>
            </div>
          </div>
        )}

        {/* Period Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Period
          </label>
          <DateRangePicker
            value={customDateRange}
            onChange={(range, selectedPeriod) => {
              if (selectedPeriod === 'custom' && range) {
                setCustomDateRange(range);
                setPeriod('custom');
              } else {
                setPeriod(selectedPeriod as any);
                setCustomDateRange(null);
              }
            }}
            maxRetentionDays={permissions?.effectiveLimits?.post_retention_days || 90}
            disabled={!currentCustomerId}
          />
        </div>

        {/* Account Selection */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-2">
            Accounts
          </label>
          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
            {/* All Accounts option */}
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedAccountIds.length === accounts.length && accounts.length > 0}
                onChange={(e) => {
                  if (e.target.checked) {
                    // Select all accounts
                    setSelectedAccountIds(accounts.map(a => a.id));
                  } else {
                    // Deselect all accounts
                    setSelectedAccountIds([]);
                  }
                }}
                disabled={!currentCustomerId}
                className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
              />
              <span className="text-xs font-medium text-gray-700">
                All Accounts ({accounts.length})
              </span>
            </label>

            {accounts.length > 0 && (
              <div className="border-t border-gray-200 pt-2 mt-2 space-y-1">
                {accounts.map((account) => (
                  <label key={account.id} className="flex items-center space-x-2 cursor-pointer py-1">
                    <input
                      type="checkbox"
                      checked={selectedAccountIds.includes(account.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedAccountIds([...selectedAccountIds, account.id]);
                        } else {
                          setSelectedAccountIds(selectedAccountIds.filter(id => id !== account.id));
                        }
                      }}
                      disabled={!currentCustomerId}
                      className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-xs text-gray-700 block truncate">
                        {account.displayName || account.username}
                      </span>
                      <span className="text-xs text-gray-500">
                        @{account.username} • {account.platformDisplayName}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Post Type Selection */}
        <div>
          <label htmlFor="posttype-select" className="block text-xs font-medium text-gray-700 mb-2">
            Post Type
          </label>
          <select
            id="posttype-select"
            value={selectedPostType}
            onChange={(e) => setSelectedPostType(e.target.value)}
            disabled={!currentCustomerId}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          >
            <option value="all">All Post Types</option>
            {availablePostTypes.map((postType: any) => (
              <option key={postType.id} value={postType.typeName}>
                {postType.displayName}
              </option>
            ))}
          </select>
        </div>

        {/* Schedule Type */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-3">
            Schedule Type
          </label>
          <div className="space-y-3">
            {/* One-time */}
            <div className="flex items-start">
              <input
                type="radio"
                id="one-time"
                name="scheduleType"
                checked={scheduleType === 'one-time'}
                onChange={() => setScheduleType('one-time')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="ml-3 flex-1">
                <label htmlFor="one-time" className="block text-xs font-medium text-gray-700 mb-2">
                  One-time
                </label>
                {scheduleType === 'one-time' && (
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="oneTimeDate" className="block text-xs text-gray-600 mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        id="oneTimeDate"
                        value={oneTimeDate}
                        onChange={(e) => setOneTimeDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label htmlFor="oneTimeTime" className="block text-xs text-gray-600 mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        id="oneTimeTime"
                        value={oneTimeTime}
                        onChange={(e) => setOneTimeTime(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Recurring */}
            <div className="flex items-start">
              <input
                type="radio"
                id="recurring"
                name="scheduleType"
                checked={scheduleType === 'recurring'}
                onChange={() => setScheduleType('recurring')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="ml-3 flex-1">
                <label htmlFor="recurring" className="block text-xs font-medium text-gray-700 mb-2">
                  Recurring
                </label>
                {scheduleType === 'recurring' && (
                  <div className="space-y-3">
                    <div>
                      <label htmlFor="frequency" className="block text-xs text-gray-600 mb-1">
                        Frequency
                      </label>
                      <select
                        id="frequency"
                        value={recurringFrequency}
                        onChange={(e) => setRecurringFrequency(e.target.value as 'daily' | 'weekly' | 'monthly')}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>

                    {recurringFrequency === 'weekly' && (
                      <div>
                        <label htmlFor="dayOfWeek" className="block text-xs text-gray-600 mb-1">
                          Day of Week
                        </label>
                        <select
                          id="dayOfWeek"
                          value={recurringDay}
                          onChange={(e) => setRecurringDay(parseInt(e.target.value))}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {[0, 1, 2, 3, 4, 5, 6].map((day) => (
                            <option key={day} value={day}>
                              {getDayOfWeekName(day)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {recurringFrequency === 'monthly' && (
                      <div>
                        <label htmlFor="dayOfMonth" className="block text-xs text-gray-600 mb-1">
                          Day of Month
                        </label>
                        <select
                          id="dayOfMonth"
                          value={recurringDay}
                          onChange={(e) => setRecurringDay(parseInt(e.target.value))}
                          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    <div>
                      <label htmlFor="recurringTime" className="block text-xs text-gray-600 mb-1">
                        Time
                      </label>
                      <input
                        type="time"
                        id="recurringTime"
                        value={recurringTime}
                        onChange={(e) => setRecurringTime(e.target.value)}
                        className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Dynamic Parameters */}
        {reportTypeDefinition?.metadata.parameters && Object.keys(reportTypeDefinition.metadata.parameters).length > 0 && (
          <div className="border-t border-gray-200 pt-4">
            <h3 className="text-xs font-semibold text-gray-700 mb-3">Report Parameters</h3>
            <div className="space-y-4">
              {Object.entries(reportTypeDefinition.metadata.parameters).map(([key, definition]) => (
                <DynamicParameterField
                  key={key}
                  paramKey={key}
                  definition={definition}
                  value={dynamicParams[key]}
                  onChange={(value) => setDynamicParams(prev => ({ ...prev, [key]: value }))}
                  disabled={!currentCustomerId}
                />
              ))}
            </div>
          </div>
        )}

        {/* Report Name */}
        <div>
          <label htmlFor="reportName" className="block text-xs font-medium text-gray-700 mb-2">
            Report Name
          </label>
          <input
            type="text"
            id="reportName"
            value={reportName}
            onChange={(e) => setReportName(e.target.value)}
            placeholder={reportTitle || 'e.g., Weekly Performance Summary'}
            maxLength={255}
            className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors"
          />
          <p className="text-xs text-gray-400 mt-1">Optional friendly name for this schedule</p>
        </div>

        {/* Email Recipients */}
        <RecipientPicker
          selectedRefs={recipientRefs}
          onChange={setRecipientRefs}
          activationId={dynamicParams.activationId || existingSchedule?.parameters?.activationId as string | undefined}
          disabled={!currentCustomerId}
        />

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={handleDrawerClose}
            disabled={isScheduling}
            className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            onClick={handleSchedule}
            disabled={!isFormValid || isScheduling}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isScheduling ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                {editMode ? 'Updating...' : 'Scheduling...'}
              </>
            ) : (
              <>
                <Clock className="h-3.5 w-3.5 mr-1.5" />
                {editMode ? 'Update Schedule' : 'Schedule Report'}
              </>
            )}
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default ScheduleReportDrawer;
